<template>
  <div class="hints-container">
    <p class="hint-title">Pistas 🔍</p>

    <!-- Patrón vocal-consonante -->
    <div class="hint-group">
      <select
        :disabled="hint1_is_disabled"
        v-model="hint1_selected"
        :class="{
          highlighted: !hint1_is_disabled && isTooltipActive1 && !hint1_selected,
          'hint-select': !(!hint1_is_disabled && isTooltipActive1 && !hint1_selected),
        }"
      >
        <option v-for="num in size" :key="num" :value="num">
          {{ num }}
        </option>
      </select>
      <button
        @click="
          hint1_selected ? $emit('showLetterType', hint1_selected as number) : showTmpTooltip1()
        "
        :disabled="hint1_is_disabled || hasLost"
        class="hint-btn"
      >
        <span
          class="tooltip"
          tabindex="0"
          @mouseenter="setTooltipActive1(true)"
          @mouseleave="setTooltipActive1(false)"
        >
          Patrón vocal-consonante
          <span v-if="!hint1_selected && isTooltipActive1" class="tooltip-text"
            >Elige una palabra primero<br /><span class="rules-link" @click="handleRulesClick">
              <u>Más información aquí</u></span
            ></span
          >
        </span>
      </button>
    </div>

    <!-- Segunda definición -->
    <div class="hint-group">
      <select
        :disabled="hint2_is_disabled || def2Empty"
        v-model="hint2_selected"
        :class="{
          highlighted: !hint2_is_disabled && isTooltipActive2 && !hint2_selected,
          'hint-select': !(!hint2_is_disabled && isTooltipActive2 && !hint2_selected),
        }"
      >
        <option v-for="num in hasdef2" :key="num" :value="num">
          {{ num }}
        </option>
      </select>
      <button
        @click="
          hint2_selected ? $emit('show-def-bis', hint2_selected as number) : showTmpTooltip2()
        "
        :disabled="hint2_is_disabled || hasLost || def2Empty"
        class="hint-btn"
      >
        <span
          class="tooltip"
          tabindex="0"
          @mouseenter="setTooltipActive2(true)"
          @mouseleave="setTooltipActive2(false)"
        >
          <div v-if="def2Empty">No hay segunda definición</div>
          <div v-if="!def2Empty">Segunda definición</div>
          <span v-if="!hint2_selected && isTooltipActive2 && !def2Empty" class="tooltip-text"
            >Elige una palabra primero<br /><span class="rules-link" @click="handleRulesClick">
              <u>Más información aquí</u></span
            ></span
          >
        </span>
      </button>
    </div>

    <!-- Descubrir letra -->
    <div class="hint-group">
      <label class="hint-label">Fila</label>
      <select
        :disabled="hint3_is_disabled"
        v-model="hint3_selected_row"
        :class="{
          highlighted: !hint3_is_disabled && isTooltipActive3 && !hint3_selected_row,
          'hint-select': !(!hint3_is_disabled && isTooltipActive3 && !hint3_selected_row),
        }"
      >
        <option v-for="num in size" :key="'row-' + num" :value="num">
          {{ num }}
        </option>
      </select>
      <label class="hint-label">Columna</label>
      <select
        :disabled="hint3_is_disabled"
        v-model="hint3_selected_col"
        :class="{
          highlighted: !hint3_is_disabled && isTooltipActive3 && !hint3_selected_col,
          'hint-select': !(!hint3_is_disabled && isTooltipActive3 && !hint3_selected_col),
        }"
      >
        <option v-for="num in size" :key="'col-' + num" :value="num">
          {{ num }}
        </option>
      </select>
      <button
        @click="
          hint3_selected_row && hint3_selected_col
            ? $emit('showLetter', hint3_selected_row as number, hint3_selected_col as number)
            : showTmpTooltip3()
        "
        :disabled="hint3_is_disabled || hasLost"
        class="hint-btn"
      >
        <span
          class="tooltip"
          tabindex="0"
          @mouseenter="setTooltipActive3(true)"
          @mouseleave="setTooltipActive3(false)"
        >
          Letra
          <span
            v-if="(!hint3_selected_row || !hint3_selected_col) && isTooltipActive3"
            class="tooltip-text-hint3"
          >
            Elige fila y columna primero<br /><span class="rules-link" @click="handleRulesClick">
              <u>Más información aquí</u></span
            >
          </span>
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

interface Props {
  size: number
  hasdef2: number[]
  def2Empty: boolean
  hint1_is_disabled: boolean
  hint2_is_disabled: boolean
  hasLost: boolean
  hint3_is_disabled: boolean
}

interface Emits {
  (e: 'showLetterType', index: number): void
  (e: 'show-def-bis', index: number): void
  (e: 'showLetter', row: number, col: number): void
  (e: 'toggle-rules', section: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const hint1_selected = ref<number | ''>('')
const hint2_selected = ref<number | ''>('')
const hint3_selected_row = ref<number | ''>('')
const hint3_selected_col = ref<number | ''>('')
const isTooltipActive1 = ref(false)
const isTooltipActive2 = ref(false)
const isTooltipActive3 = ref(false)
const timeTooltip = 4000

const setTooltipActive1 = (active: boolean) => {
  isTooltipActive1.value = active
}

const setTooltipActive2 = (active: boolean) => {
  isTooltipActive2.value = active
}

const setTooltipActive3 = (active: boolean) => {
  isTooltipActive3.value = active
}

const showTmpTooltip1 = (): void => {
  isTooltipActive1.value = true
  setTimeout(() => {
    isTooltipActive1.value = false
  }, timeTooltip)
}

const showTmpTooltip2 = (): void => {
  isTooltipActive2.value = true
  setTimeout(() => {
    isTooltipActive2.value = false
  }, timeTooltip)
}

const showTmpTooltip3 = (): void => {
  isTooltipActive3.value = true
  setTimeout(() => {
    isTooltipActive3.value = false
  }, timeTooltip)
}

const handleRulesClick = (): void => {
  emit('toggle-rules', 'pista-section')
}
</script>

<style scoped>
.hints-container {
  position: relative;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  padding: 16px;
  margin: 10px auto;
  width: fit-content;
  max-width: 90%;
}

.hint-title {
  text-align: center;
  font-size: 20px;
  font-weight: normal;
  justify-content: center;
  margin-bottom: 12px;
  margin-top: 0px;
}

.hint-group {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;
}

.hint-label {
  font-size: 1em;
  margin-right: 0px;
}

.hint-select {
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1em;
  transition: border-color 0.3s ease;
}

.highlighted {
  padding: 4px 8px;
  border: 2px solid #ccc;
  border-radius: 4px;
  font-size: 1em;
  transition: border-color 0.3s ease;
  border-color: #4caf50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.hint-btn {
  padding: 6px 12px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1em;
  transition: background-color 0.3s;
  flex: 1;
}

.hint-btn:hover:not(:disabled) {
  background-color: #45a049;
}

.hint-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.tooltip {
  position: relative;
  display: inline-block;
  cursor: pointer;
  outline: none;
}

.tooltip-text {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  left: 50%;
  background-color: #333;
  color: #fff;
  padding: 8px;
  border-radius: 4px;
  font-size: 14px;
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;
  white-space: nowrap;
  z-index: 1;
  transform: translateX(-50%) translateY(-15px);
}

.tooltip:hover .tooltip-text,
.tooltip:focus .tooltip-text {
  visibility: visible;
  opacity: 1;
}

@media (min-width: 1200px) {
  .hints-container {
    position: absolute;
    top: 10px;
    right: 20px;
  }
}

@media (max-width: 1200px) {
  .hints-container {
    position: relative;
    margin: 20px auto;
    width: 90%;
  }
}

@media (max-width: 1200px) {
  .tooltip-text-hint3 {
    transform: translateX(-90px) translateY(30px) !important;
  }
}

.tooltip-text-hint3 {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  left: 50%;
  background-color: #333;
  color: #fff;
  padding: 8px;
  border-radius: 4px;
  font-size: 14px;
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;
  white-space: nowrap;
  z-index: 1;
  transform: translateX(-120px) translateY(30px);
}

.tooltip:hover .tooltip-text-hint3,
.tooltip:focus .tooltip-text-hint3 {
  visibility: visible;
  opacity: 1;
}
</style>
